import React from "react";
import { motion } from "framer-motion";
import StudyHeatMap from "../../StudyHeatMap";

const authors = [
  {
    name: "Chunlong Mu",
    role: "Lead Researcher and Primary Investigator",
    description:
      "Chunlong Mu is the Principal Investigator for the SeizureBiomeDB project, leading the research and development of the database, securing funding, and overseeing the overall direction of the project.",
    image: "/chunlong.jpeg",
  },
  {
    name: "Zehaan Walji",
    role: "Lead Developer",
    description:
      "Zehaan Walji is the lead developer for the SeizureBiomeDB project, responsible for the technical architecture, database design, and implementation of the web application.",
    image: "/zehaan.png",
  },
  {
    name: "Parshva Dave",
    role: "Lead Developer",
    description:
      "Parshva Dave is a lead developer for the SeizureBiomeDB project, focusing on the frontend development and user experience design of the web application.",
    image: "/parshva.jpg",
  },
  {
    name: "Jane Shearer",
    role: "Researcher",
    description:
      "Jane Shearer is the lead supervisor for this project, overseeing the research and development of the SeizureBiomeDB.",
    image: "/jane_shearer.jpg",
  },
  {
    name: "Meherab Ali",
    role: "Graphical Designer",
    description:
      "Meherab Ali is a graphical designer for the SeizureBiomeDB project, responsible for creating the visual design and user interface of the web application.",
    image: "/meherab.png",
  },
  {
    name: "Omar Negmeldin",
    role: "Graphical Designer",
    description:
      "Omar Negmeldin is a graphical designer for the SeizureBiomeDB project, responsible for creating the visual design and user interface of the web application.",
    image: "/omar.png",
  },
  {
    name: "Jackson Qi",
    role: "Data Operations Associate",
    description:
      "Jackson Qi is a third-year Biological Sciences student at the University of Calgary, working on the data compilation and entry for the site.",
    image: "/jackson.jpeg",
  }
];

export default function About() {
  const lead = authors[0];
  const team = authors.slice(1);

  return (
    <div
      style={{
        padding: "3rem 1.5rem",
        maxWidth: "1100px",
        margin: "0 auto",
        textAlign: "center",
      }}
    >
      {/* Heading */}
      <motion.h2
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 60, damping: 15 }}
        style={{
          color: "#0079C0",
          fontSize: "2.5rem",
          fontWeight: "700",
          marginBottom: "1rem",
        }}
      >
        Meet the Team
      </motion.h2>

      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ type: "spring", stiffness: 80, damping: 12 }}
        style={{
          width: "80px",
          height: "4px",
          backgroundColor: "#0079C0",
          margin: "0 auto 2.5rem auto",
          borderRadius: "2px",
          transformOrigin: "left center",
        }}
      />

      {/* Lead profile */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 60, damping: 14 }}
        style={{
          backgroundColor: "#ffffff",
          padding: "2rem",
          borderRadius: "16px",
          display: "flex",
          alignItems: "center",
          gap: "2rem",
          marginBottom: "3rem",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
          border: "1px solid #e2e8f0",
          textAlign: "left",
          flexWrap: "wrap",
        }}
      >
        <img
          src={lead.image}
          alt={lead.name}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/default.png";
          }}
          style={{
            width: "140px",
            height: "140px",
            borderRadius: "50%",
            objectFit: "cover",
            border: "3px solid #0079C0",
          }}
        />
        <div>
          <h3 style={{ color: "#0079C0", fontSize: "1.8rem", margin: 0 }}>
            {lead.name}
          </h3>
          <p style={{ fontWeight: "600", color: "#333", margin: "0.5rem 0" }}>
            {lead.role}
          </p>
          <p style={{ fontSize: "1rem", color: "#555", maxWidth: "650px" }}>
            {lead.description}
          </p>
        </div>
      </motion.div>

      {/* Team Grid */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "2rem",
        }}
      >
        {team.map((author, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              type: "spring",
              stiffness: 60,
              damping: 14,
              delay: 0.15 + index * 0.08,
            }}
            whileHover={{ scale: 1.015 }}
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              padding: "1.5rem",
              width: "260px",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
              border: "1px solid #e2e8f0",
              textAlign: "center",
            }}
          >
            <img
              src={author.image}
              alt={author.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/default.png";
              }}
              style={{
                width: "90px",
                height: "90px",
                borderRadius: "50%",
                objectFit: "cover",
                marginBottom: "1rem",
                border: "2px solid #0079C0",
              }}
            />
            <h3 style={{ color: "#0079C0", fontSize: "1.2rem", marginBottom: "0.3rem" }}>
              {author.name}
            </h3>
            <p style={{ fontWeight: "600", color: "#333", marginBottom: "0.5rem" }}>
              {author.role}
            </p>
            <p style={{ fontSize: "0.9rem", color: "#555" }}>
              {author.description}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Heatmap Section */}
      <div style={{ width: "100%", marginTop: "4rem" }}>
        <h2 style={{ color: "#0079C0", fontSize: "2rem", marginBottom: "0.5rem" }}>
          Global Study Distribution
        </h2>
        <p style={{ color: "#555", marginBottom: "1rem" }}>
          Heatmap showing number of studies per country
        </p>

        <div style={{ marginBottom: "1rem", fontSize: "0.9rem", color: "#555" }}>
          <span style={{
            display: "inline-block", width: "12px", height: "12px",
            backgroundColor: "#c6dbef", marginRight: "6px", borderRadius: "2px"
          }}></span> Low&nbsp;&nbsp;
          <span style={{
            display: "inline-block", width: "12px", height: "12px",
            backgroundColor: "#fdae61", marginRight: "6px", borderRadius: "2px"
          }}></span> Medium&nbsp;&nbsp;
          <span style={{
            display: "inline-block", width: "12px", height: "12px",
            backgroundColor: "#d73027", marginRight: "6px", borderRadius: "2px"
          }}></span> High Density
        </div>

        <StudyHeatMap />
      </div>
    </div>
  );
}
